/**
 * Entrada do frontend do Nexora Grid (React + Vite, ADR-0002).
 *
 * Criado em: 16/10/2026 · Última modificação: 26/10/2026 · Versão: 1.23.0
 *
 * US-401: login, navegação por perfil entre CCO, Rede, SCADA 3D, Cadastro e Videowall, com o design system
 * US-304: CCO › Relatórios (relatório diário do Data & Analytics Agent).
 * RF-09: sem sessão, `/redefinir-senha` e `/confirmar-cadastro` abrem a definição de senha pelo link do e-mail.
 * US-3101: `/cadastrar-aparelho#<token>` (com ou sem sessão) cadastra a passkey do celular de um canal de chat.
 * US-3103: `/confirmar-acao#<token>` (com ou sem sessão) confirma com a biometria uma ação pedida pelo chat.
 * US-1804: Administração › Usuários (`/admin/usuarios`, novo e detalhe). US-1901: Administração › Empresas. US-2001: Administração › Dispositivos. US-2003: Administração › Conectores. US-2101: Regras de alarme (`/regras`). US-2102: Manobra automática (`/manobra-automatica`). US-2103: Administração › Canais e alertas. US-2201: assistente de implantação. US-3002: Administração › Feriados.
 * (`frontend/design`). As telas são preenchidas pelas histórias seguintes da Sprint 4.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "../design/tokens.css";
import "../design/componentes.css";
import "./estilo.css";
import { Canais } from "./admin/Canais";
import { Feriados } from "./admin/Feriados";
import { ConectorDetalhe, ConectorNovo, Conectores } from "./admin/Conectores";
import { ManobraAutomatica } from "./regras/ManobraAutomatica";
import { RegraDetalhe, RegraNova, Regras } from "./regras/Regras";
import { DispositivoDetalhe, DispositivoNovo, Dispositivos } from "./admin/Dispositivos";
import { EmpresaDetalhe, EmpresaNova, Empresas } from "./admin/Empresas";
import { Implantacao } from "./admin/Implantacao";
import { UsuarioDetalhe } from "./admin/UsuarioDetalhe";
import { UsuarioNovo } from "./admin/UsuarioNovo";
import { Usuarios } from "./admin/Usuarios";
import { Detalhe } from "./cadastro/Detalhe";
import { Importar } from "./cadastro/Importar";
import { Lista } from "./cadastro/Lista";
import { Novo } from "./cadastro/Novo";
import { Alarmes } from "./cco/Alarmes";
import { Analise } from "./cco/Analise";
import { Relatorios } from "./cco/Relatorios";
import { Seguranca } from "./cco/Seguranca";
import { Qualidade } from "./cco/Qualidade";
import { Gestor } from "./cco/Gestor";
import { MapaComando } from "./cco/MapaComando";
import { Referencias } from "./admin/Referencias";
import { Paineis } from "./paineis/Paineis";
import { Treinamento } from "./treinamento/Treinamento";
import { Cco } from "./cco/Telas";
import { Tendencias } from "./cco/Tendencias";
import { GraficoReferencia } from "./cco/GraficoReferencia";
import { Layout } from "./Layout";
import { MenuCompleto } from "./Menu";
import { Rede } from "./rede/Rede";
import { Scada3d } from "./scada3d/Scada3d";
import { EmConstrucao } from "./paginas/EmConstrucao";
import { CadastroAparelho } from "./paginas/CadastroAparelho";
import { ConfirmarAcao } from "./paginas/ConfirmarAcao";
import { DefinirSenha } from "./paginas/ContaEmail";
import { Login } from "./paginas/Login";
import { TrocarSenha } from "./paginas/TrocarSenha";
import { Videowall } from "./paginas/Videowall";
import { ProvedorSessao, useSessao } from "./sessao";
import { pode } from "./permissoes";
import { podeAcessarRota } from "./acessoRotas";
import type { ReactNode } from "react";
import { MarcaProvider } from "./marca";
import { Marca } from "./admin/Marca";

function Acesso({ permitido, children }: { permitido: boolean; children: ReactNode }) {
  return permitido ? children : <section className="pagina"><h1>Acesso indisponível</h1><p>Esta tela não está liberada para sua conta.</p></section>;
}

function Rotas() {
  const { usuario } = useSessao();
  const local = useLocation();
  // US-3101: aberta no celular pelo link do chat; não depende do login
  if (window.location.pathname === "/cadastrar-aparelho") return <CadastroAparelho />;
  if (window.location.pathname === "/confirmar-acao") return <ConfirmarAcao />;
  if (window.location.pathname === "/redefinir-senha") return <DefinirSenha tipo="recuperacao" />;
  if (window.location.pathname === "/confirmar-cadastro") return <DefinirSenha tipo="confirmacao" />;
  if (!usuario) {
    return <Login />;
  }
  if (usuario.trocar_senha) return <TrocarSenha />;  // senha provisória: só esta tela até a troca
  return (
    <Routes>
      <Route element={<Acesso permitido={podeAcessarRota(usuario, local.pathname + local.search)}><Layout /></Acesso>}>
        <Route path="/" element={<Navigate to={pode.verTreinamento(usuario) ? "/treinamento" : "/cco"} replace />} />
        <Route path="/cco" element={<Cco />}>
          {/* GUS-1508: a tela nova (protótipo aprovado v4) é desenhada pelo layout do CCO nestes caminhos (uma instância só) */}
          <Route index element={null} />
          <Route path="cidade/:municipio" element={null} />
          <Route path="unidade/:unidade" element={null} />
          <Route path="equipamento/:device" element={null} />
          <Route path="equipamento/:device/diagnostico" element={null} />
          <Route path="tendencias" element={<Tendencias />} />
          <Route path="grafico" element={<GraficoReferencia />} />
          <Route path="alarmes" element={<Alarmes />} />
          <Route path="analise" element={<Analise />} />
          <Route path="relatorios" element={<Relatorios />} />
          <Route path="qualidade" element={<Qualidade />} />
          <Route path="gestor" element={<Gestor />} />
          <Route path="mapa" element={<Acesso permitido={pode.verObservabilidadeLocal(usuario)}><MapaComando /></Acesso>} />
          <Route path="seguranca" element={<Seguranca />} />
        </Route>
        <Route path="/rede" element={<Rede />} />
        <Route path="/scada3d" element={<Scada3d />} />
        <Route path="/cadastro" element={<Lista />} />
        <Route path="/cadastro/novo" element={<Acesso permitido={pode.editarCadastro(usuario) || pode.cadastrarEmCampo(usuario)}><Novo /></Acesso>} />
        <Route path="/cadastro/importar" element={<Acesso permitido={pode.editarCadastro(usuario)}><Importar /></Acesso>} />
        <Route path="/cadastro/:id" element={<Detalhe />} />
        <Route path="/videowall" element={<Videowall />} />
        <Route path="/meus-paineis" element={<Acesso permitido={pode.verPaineis(usuario)}><Paineis /></Acesso>} />
        <Route path="/treinamento" element={<Acesso permitido={pode.verTreinamento(usuario)}><Treinamento /></Acesso>} />
        {/* US-1804 (ADR-0014): back-office — Administração */}
        <Route path="/admin/usuarios" element={<Acesso permitido={pode.verUsuarios(usuario)}><Usuarios /></Acesso>} />
        <Route path="/admin/usuarios/novo" element={<Acesso permitido={pode.gerenciarUsuarios(usuario)}><UsuarioNovo /></Acesso>} />
        <Route path="/admin/usuarios/:id" element={<Acesso permitido={pode.verUsuarios(usuario)}><UsuarioDetalhe /></Acesso>} />
        <Route path="/admin/empresas" element={<Acesso permitido={pode.verEstrutura(usuario)}><Empresas /></Acesso>} />
        <Route path="/admin/empresas/nova" element={<Acesso permitido={pode.editarEstrutura(usuario)}><EmpresaNova /></Acesso>} />
        <Route path="/admin/empresas/:id" element={<Acesso permitido={pode.verEstrutura(usuario)}><EmpresaDetalhe /></Acesso>} />
        <Route path="/admin/empresas/:id/implantacao" element={<Acesso permitido={pode.verEstrutura(usuario)}><Implantacao /></Acesso>} />
        <Route path="/admin/dispositivos" element={<Acesso permitido={pode.verEstrutura(usuario)}><Dispositivos /></Acesso>} />
        <Route path="/admin/dispositivos/novo" element={<Acesso permitido={pode.editarEstrutura(usuario)}><DispositivoNovo /></Acesso>} />
        <Route path="/admin/dispositivos/:id" element={<DispositivoDetalhe />} />
        <Route path="/admin/conectores" element={<Acesso permitido={pode.verEstrutura(usuario)}><Conectores /></Acesso>} />
        <Route path="/admin/canais" element={<Acesso permitido={pode.verUsuarios(usuario)}><Canais /></Acesso>} />
        <Route path="/admin/feriados" element={<Acesso permitido={pode.editarFeriados(usuario) || pode.importarFeriadosNacionais(usuario)}><Feriados /></Acesso>} />
        <Route path="/admin/marca" element={<Acesso permitido={pode.verMarca(usuario)}><Marca /></Acesso>} />
        <Route path="/admin/referencias" element={<Acesso permitido={pode.importarReferencias(usuario) || pode.verEstrutura(usuario)}><Referencias /></Acesso>} />
        <Route path="/regras" element={<Regras />} />
        <Route path="/regras/nova" element={<Acesso permitido={pode.editarRegras(usuario)}><RegraNova /></Acesso>} />
        <Route path="/regras/:id" element={<RegraDetalhe />} />
        <Route path="/manobra-automatica" element={<ManobraAutomatica />} />
        <Route path="/admin/conectores/novo" element={<Acesso permitido={pode.editarEstrutura(usuario)}><ConectorNovo /></Acesso>} />
        <Route path="/admin/conectores/:nome" element={<Acesso permitido={pode.verEstrutura(usuario)}><ConectorDetalhe /></Acesso>} />
        <Route path="/menu" element={<MenuCompleto usuario={usuario} />} />
        <Route path="*" element={<EmConstrucao titulo="Página não encontrada" historia="confira o endereço" />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById("raiz")!).render(
  <StrictMode>
    <ProvedorSessao>
      <MarcaProvider>
        <BrowserRouter>
          <Rotas />
        </BrowserRouter>
      </MarcaProvider>
    </ProvedorSessao>
  </StrictMode>,
);
